// AgriVoLED game engine — a TypeScript port of the Urvara simulation.
//
// Ported from (and kept faithful to) the Python in the Urvara folder:
//   rootstock_sim.py  soil organic matter, drought buffering, timeliness
//   planner.py        labour per operation, harvest capacity
//   prices.py         twelve-month price curves driven by UK supply share
//   markets.py        channel ladder, soft saturation, marginal allocation
//   league.py         the triple score
//
// A turn is ONE WEEK. Crop windows in the data are week-based, so weeks keep
// the agronomy honest; the UI advances several at a time.

import { CROPS, CHANNELS, DATA, Crop } from './data';

export const WEEKS = 52;
export const POP = 50_000;            // addressable people, as in the market study
const FILL_PENALTY = 0.35;            // markets.py
const STORE_SHRINK = 0.005;

export const wk = (n: number) => ((n - 1) % WEEKS + WEEKS) % WEEKS + 1;
export const monthOf = (w: number) => Math.min(11, Math.floor((wk(w) - 1) / WEEKS * 12));
export const cropById = (id: string) => CROPS.find(c => c.id === id);

/* ── soil (rootstock_sim.py) ───────────────────────────────────────────── */
export const soilModifier = (som: number) => Math.max(0.6, Math.min(1.3, 0.60 + 0.14 * som));
export const droughtSensitivity = (som: number) => Math.max(0.45, Math.min(1, 1.25 - 0.13 * som));

/* ── market (markets.py) ───────────────────────────────────────────────── */
export function saturation(qty: number, cap: number, elasticity: number) {
  if (qty <= 0 || cap <= 0) return 1;
  const fill = qty / cap;
  return fill <= 1
    ? 1 - FILL_PENALTY * fill * fill
    : (1 - FILL_PENALTY) * Math.pow(1 / fill, 1 / Math.abs(elasticity));
}

export type Allocation = { channel: string; kg: number; mult: number };

/** Spread a week's produce across channels by equalising marginal revenue.
 *  Filling the best channel first produces a non-monotonic price — see the
 *  market study. */
export function allocate(qty: number, pop = POP, unlocked?: Set<string>): {
  rows: Allocation[]; blended: number;
} {
  if (qty <= 0) return { rows: [], blended: 0 };
  const avail = CHANNELS.filter(c => !unlocked || unlocked.has(c.id));
  const caps: Record<string, number> = {};
  const held: Record<string, number> = {};
  avail.forEach(c => {
    caps[c.id] = c.capPer1000 >= 1e8 ? Infinity : c.capPer1000 * pop / 1000;
    held[c.id] = 0;
  });
  const chunks = 40, dq = qty / chunks;
  for (let i = 0; i < chunks; i++) {
    let best = avail[0], bestMr = -Infinity;
    for (const c of avail) {
      const cap = caps[c.id];
      const mr = cap === Infinity ? c.mult
        : ((c.mult * saturation(held[c.id] + dq, cap, c.elasticity) * (held[c.id] + dq))
         - (c.mult * saturation(held[c.id], cap, c.elasticity) * held[c.id])) / dq;
      if (mr > bestMr) { bestMr = mr; best = c; }
    }
    held[best.id] += dq;
  }
  const rows: Allocation[] = [];
  for (const c of avail) {
    const q = held[c.id];
    if (q <= 1e-9) continue;
    const cap = caps[c.id];
    rows.push({ channel: c.id, kg: q,
      mult: cap === Infinity ? c.mult : c.mult * saturation(q, cap, c.elasticity) });
  }
  const blended = rows.reduce((a, r) => a + r.kg * r.mult, 0) / qty;
  return { rows, blended };
}

export const priceIndex = (crop: Crop, week: number) => crop.priceIndex[monthOf(week)] ?? 1;

/* ── plantings ─────────────────────────────────────────────────────────── */
export type Planting = {
  id: number; cropId: string; bed: number; sownWeek: number; areaM2: number;
  harvestedKg: number; lostKg: number;
};

export const plantWeek = (p: Planting, c: Crop) => c.method === 'transplant' ? p.sownWeek + c.prop : p.sownWeek;
export const harvestStart = (p: Planting, c: Crop) => p.sownWeek + c.prop + c.grow;
export const harvestEnd = (p: Planting, c: Crop) => harvestStart(p, c) + Math.max(1, c.harv) - 1;
export const isOccupying = (p: Planting, c: Crop, week: number) => {
  const s = plantWeek(p, c), e = harvestEnd(p, c);
  for (let w = s; w <= e; w++) if (wk(w) === wk(week)) return true;
  return false;
};
export const inSowWindow = (c: Crop, week: number) => {
  const w = wk(week);
  return c.sowLo <= c.sowHi ? (w >= c.sowLo && w <= c.sowHi) : (w >= c.sowLo || w <= c.sowHi);
};
export function frostRisk(c: Crop, week: number) {
  if (c.protected || c.family === 'Fungi' || c.family === 'Perennial') return false;
  const tender = c.family === 'Solanaceae' || c.family === 'Cucurbit';
  if (!tender) return false;
  const pw = c.method === 'transplant' ? week + c.prop : week;
  return wk(pw) < DATA.frost.last || wk(week + c.prop + c.grow) > DATA.frost.first;
}

/* ── labour (planner.py) ───────────────────────────────────────────────── */
export function labourForWeek(p: Planting, c: Crop, week: number): number {
  const L = DATA.labour;
  const start = plantWeek(p, c);
  let h = 0;
  if (wk(start - 1) === wk(week)) h += p.areaM2 * (L.bedprep + L.compost);
  if (wk(p.sownWeek) === wk(week)) h += p.areaM2 * L.sow * (c.method === 'tuber' ? 2.5 : 1);
  if (isOccupying(p, c, week)) h += p.areaM2 * L.water;
  const span = Math.max(1, c.grow);
  const passes = Math.max(1, Math.round(span / 10 * 4));
  for (let i = 0; i < passes; i++) {
    if (wk(start + Math.round(span * (i + 0.5) / passes)) === wk(week)) h += p.areaM2 * L.weedPass;
  }
  return h * L.overhead;
}

export function weeklyYield(p: Planting, c: Crop, week: number, som: number): number {
  const hs = harvestStart(p, c), hw = Math.max(1, c.harv);
  let inWindow = false;
  for (let i = 0; i < hw; i++) if (wk(hs + i) === wk(week)) inWindow = true;
  if (!inWindow) return 0;
  return (c.yieldKgM2 * p.areaM2 / hw) * soilModifier(som);
}

/* ── events ────────────────────────────────────────────────────────────── */
export type GameEvent = { week: number; kind: string; text: string; severity: 'good' | 'warn' | 'bad' };

/* ── state ─────────────────────────────────────────────────────────────── */
export type Bed = { id: number; areaM2: number; som: number; protected: boolean };

export type GameState = {
  week: number; year: number;
  cash: number;
  beds: Bed[];
  plantings: Planting[];
  log: GameEvent[];
  totals: { revenue: number; labourCost: number; inputCost: number; kgSold: number; kgLost: number };
  workers: number;
  nextId: number;
  seed: number;
};

export const HOURS_PER_WORKER_WEEK = 36;

export function newGame(seed = 20261025): GameState {
  return {
    week: 10, year: 1,   // early March — when a market garden actually starts
    cash: DATA.start.cash,
    beds: [
      { id: 0, areaM2: 100, som: 3.0, protected: false },
      { id: 1, areaM2: 100, som: 3.0, protected: false },
      { id: 2, areaM2: 100, som: 3.0, protected: false },
      { id: 3, areaM2: 100, som: 3.0, protected: false },
      { id: 4, areaM2: 84,  som: 3.0, protected: true },   // the 6 × 14 m polytunnel
    ],
    plantings: [],
    log: ([{ week: 10, kind: 'start', severity: 'good' as const,
             text: 'Early March. Four no-dig beds, one polytunnel and £2,500. Sow something.' }] as GameEvent[]),
    totals: { revenue: 0, labourCost: 0, inputCost: 0, kgSold: 0, kgLost: 0 },
    workers: 1,
    nextId: 1,
    seed,
  };
}

/** Deterministic pseudo-random, so a season replays identically — the
 *  shared-seed fairness idea from the competition design. */
function rnd(seed: number, salt: number) {
  const x = Math.sin(seed * 9301 + salt * 49297) * 233280;
  return x - Math.floor(x);
}

export function canPlant(s: GameState, bedId: number, cropId: string): string | null {
  const c = cropById(cropId); if (!c) return 'Unknown crop';
  const bed = s.beds.find(b => b.id === bedId); if (!bed) return 'No such bed';
  if (c.protected && !bed.protected) return `${c.name} needs the polytunnel`;
  if (!inSowWindow(c, s.week)) return `${c.name} is sown in weeks ${c.sowLo}–${c.sowHi}`;
  if (frostRisk(c, s.week)) return `${c.name} is tender — frost would take it`;
  const busy = s.plantings.some(p => p.bed === bedId && isOccupying(p, cropById(p.cropId)!, s.week));
  if (busy) return 'That bed is already in use';
  const cost = seedCost(c, bed.areaM2);
  if (cost > s.cash) return `Not enough cash (needs £${cost.toFixed(0)})`;
  return null;
}

export const seedCost = (c: Crop, areaM2: number) =>
  Math.round(areaM2 * (c.method === 'tuber' ? 0.55 : c.method === 'transplant' ? 0.30 : 0.12));

export function plant(s: GameState, bedId: number, cropId: string): GameState {
  const err = canPlant(s, bedId, cropId);
  if (err) return s;
  const c = cropById(cropId)!;
  const bed = s.beds.find(b => b.id === bedId)!;
  const cost = seedCost(c, bed.areaM2);
  return {
    ...s,
    cash: s.cash - cost,
    nextId: s.nextId + 1,
    plantings: [...s.plantings, { id: s.nextId, cropId, bed: bedId, sownWeek: s.week,
                                  areaM2: bed.areaM2, harvestedKg: 0, lostKg: 0 }],
    totals: { ...s.totals, inputCost: s.totals.inputCost + cost },
    log: ([{ week: s.week, kind: 'plant', severity: 'good' as const,
             text: `Sowed ${c.name} in bed ${bedId + 1} (£${cost})` },
            ...s.log] as GameEvent[]).slice(0, 60),
  };
}

export type WeekResult = {
  labourHours: number; capacityHours: number; overloaded: boolean;
  kgReady: number; kgSold: number; kgLost: number;
  revenue: number; labourCost: number; blended: number;
  events: GameEvent[];
};

export function advanceWeek(s: GameState): { state: GameState; result: WeekResult } {
  const week = s.week;
  const events: GameEvent[] = [];
  const capacity = s.workers * HOURS_PER_WORKER_WEEK;

  // field labour
  let fieldHours = 0;
  for (const p of s.plantings) {
    const c = cropById(p.cropId); if (!c) continue;
    fieldHours += labourForWeek(p, c, week);
  }

  // what is ready this week
  const ready: { p: Planting; c: Crop; kg: number }[] = [];
  for (const p of s.plantings) {
    const c = cropById(p.cropId); if (!c) continue;
    const bed = s.beds.find(b => b.id === p.bed)!;
    const kg = weeklyYield(p, c, week, bed.som);
    if (kg > 0) ready.push({ p, c, kg });
  }
  let kgReady = ready.reduce((a, r) => a + r.kg, 0);

  // weather, deterministic from the seed
  const roll = rnd(s.seed, week + s.year * 100);
  let lossFactor = 0;
  const mo = monthOf(week);
  if (roll > 0.94 && mo >= 5 && mo <= 7) {
    const avgSom = s.beds.reduce((a, b) => a + b.som, 0) / s.beds.length;
    lossFactor = 0.35 * droughtSensitivity(avgSom);
    events.push({ week, kind: 'drought', severity: 'bad',
      text: `Drought. ${(lossFactor * 100).toFixed(0)}% of this week's crop lost — deeper soil would have held more water.` });
  } else if (roll < 0.05 && (wk(week) < DATA.frost.last || wk(week) > DATA.frost.first)) {
    lossFactor = 0.25;
    events.push({ week, kind: 'frost', severity: 'bad',
      text: 'A hard frost. A quarter of what was standing is gone.' });
  }
  kgReady *= (1 - lossFactor);

  // harvest capacity: hours left after field work
  const harvestHours = Math.max(0, capacity - fieldHours);
  const L = DATA.labour;
  const kgPerHour = 1 / (L.harvestHrKg + L.packHrKg);
  const kgCapacity = harvestHours * kgPerHour;
  const kgSold = Math.min(kgReady, kgCapacity);
  const kgLost = Math.max(0, kgReady - kgSold);
  if (kgLost > 1) {
    events.push({ week, kind: 'unpicked', severity: 'warn',
      text: `${kgLost.toFixed(0)} kg left unpicked — not enough hours in the week.` });
  }

  // sell: seasonal price × channel ladder
  let revenue = 0, blended = 0;
  if (kgSold > 0) {
    const share = kgSold / Math.max(kgReady, 1e-9);
    const { blended: b } = allocate(kgSold);
    blended = b;
    for (const r of ready) {
      const sold = r.kg * (1 - lossFactor) * share;
      revenue += sold * r.c.price * priceIndex(r.c, week) * b;
    }
  }

  const usedHours = Math.min(capacity, fieldHours + kgSold / kgPerHour);
  const labourCost = usedHours * L.rate;

  // soil drifts with what is growing
  const beds = s.beds.map(bed => {
    const occupied = s.plantings.some(p => p.bed === bed.id &&
      isOccupying(p, cropById(p.cropId)!, week));
    const legume = s.plantings.some(p => p.bed === bed.id && cropById(p.cropId)?.nFixer &&
      isOccupying(p, cropById(p.cropId)!, week));
    let d = occupied ? -0.0012 : 0.0008;          // cropping draws down, resting recovers
    if (legume) d += 0.0022;
    return { ...bed, som: Math.max(0.8, Math.min(8, bed.som + d)) };
  });

  // retire finished plantings
  const plantings = s.plantings.filter(p => {
    const c = cropById(p.cropId); if (!c) return false;
    return wk(harvestEnd(p, c)) !== wk(week) || harvestEnd(p, c) > week;
  });

  const nextWeek = week >= WEEKS ? 1 : week + 1;
  const nextYear = week >= WEEKS ? s.year + 1 : s.year;
  if (fieldHours > capacity) {
    events.push({ week, kind: 'overload', severity: 'warn',
      text: `Field work alone needed ${fieldHours.toFixed(0)} h against ${capacity} h available.` });
  }

  const state: GameState = {
    ...s,
    week: nextWeek, year: nextYear, beds, plantings,
    cash: s.cash + revenue - labourCost,
    totals: {
      revenue: s.totals.revenue + revenue,
      labourCost: s.totals.labourCost + labourCost,
      inputCost: s.totals.inputCost,
      kgSold: s.totals.kgSold + kgSold,
      kgLost: s.totals.kgLost + kgLost,
    },
    log: [...events, ...s.log].slice(0, 60),
  };

  return { state, result: { labourHours: fieldHours, capacityHours: capacity,
    overloaded: fieldHours > capacity, kgReady, kgSold, kgLost, revenue,
    labourCost, blended, events } };
}

/* ── triple score (league.py) ──────────────────────────────────────────── */
export function score(s: GameState) {
  const profit = s.cash - DATA.start.cash;
  const avgSom = s.beds.reduce((a, b) => a + b.som, 0) / s.beds.length;
  const families = new Set(s.plantings.map(p => cropById(p.cropId)?.family)).size;
  const weeksSupplied = s.totals.kgSold > 0 ? Math.min(52, s.totals.kgSold / 40) : 0;

  const sProfit = Math.max(0, Math.min(100, profit / 90));
  const sPlanet = Math.max(0, Math.min(100, (avgSom - 1.5) * 22 + families * 4));
  const sPeople = Math.max(0, Math.min(100, weeksSupplied * 1.6 + families * 5
    - (s.totals.kgLost / Math.max(1, s.totals.kgSold)) * 40));
  const trio = [sProfit, sPlanet, sPeople];
  return {
    profit: sProfit, planet: sPlanet, people: sPeople,
    total: trio.reduce((a, b) => a + b, 0) + Math.min(...trio),   // weakest counts twice
    cashProfit: profit, avgSom,
  };
}
